import React from 'react';
import { FormikProps } from 'formik';
import { Box, CheckboxV2, DatePickerInput, Flex, FormControl, InputV2, SpacingStack, TextV2, TooltipV2, useToast } from '@withjoy/joykit';
import { Calendar, Help, WarningTriangle } from '@withjoy/joykit/icons';
import { PagePhoto } from '@website-editor/components/PagePhoto';
import { useTranslation } from '@shared/core';
import { useHotelPhoto } from '../../hooks/useHotelPhoto';
import { pxToRem, Theme, useTheme } from '@withjoy/joykit/theme';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { AccommodationPlaceData } from '../../hooks/useAlignAccommodationPlaceData';
import StyledToggle from '../StyledToggle';
import { useFieldErrors } from '@shared/createEvent/hooks/useFieldErrors';
import { HotelRoomBlock, TravelMapBlockType } from '../../Accommodations.types';
import { DateRangeFields } from '../DateRangeFields';
import { useAuth } from '@shared/components/AuthProvider';
import { ResponsiveValue } from '@withjoy/joykit/styled-system';
import { ThemeValue } from 'styled-system';

interface BaseFormFields
  extends HotelRoomBlock,
    Readonly<{
      isFavorite?: boolean | null;
      customPhotoId?: string | null;
      photoUrl?: string | null;
      [key: string]: unknown;
    }> {}

interface HotelRoomBlockFormProps<T extends BaseFormFields>
  extends Readonly<{
    formik: FormikProps<T>;
    containerId: string;
    data: AccommodationPlaceData;
    type: TravelMapBlockType;
    isInDialog?: boolean;
    isEditMode?: boolean;
  }> {}

export function HotelRoomBlockForm<T extends BaseFormFields>({ formik, containerId, data, type, isInDialog, isEditMode }: HotelRoomBlockFormProps<T>): JSX.Element {
  const { tq } = useTranslation('travelMap');
  const theme = useTheme();

  const { toast } = useToast();

  const { isSuperAdmin } = useAuth();
  const isDisabledForCouples = formik?.values?.isJoyManagedRoomBlock && !isSuperAdmin;
  const isHotel = type === TravelMapBlockType.travelMapAccommodationsHotel;

  const getFieldErrorProps = useFieldErrors<BaseFormFields>(formik);

  const onSaveError = useEventCallback((message: string) => {
    toast(message, { icon: <WarningTriangle /> });
  });

  const onDateChange = useEventCallback(([startDate, endDate]: Readonly<[Date | undefined, Date | undefined]>) => {
    formik.setFieldValue('checkInDate', startDate);
    formik.setFieldValue('checkOutDate', endDate);
  });

  const { hotelPhoto, isUploadingPhoto, onUpdateHotelPhoto, onDeleteHotelPhoto, onResetHotelPhoto, hasDefaultPhoto } = useHotelPhoto({
    containerId,
    formik,
    onSaveError,
    hotelPhoto: data.isPhotoHidden ? undefined : data?.displayPhoto?.url,
    defaultPhoto: data?.photo?.url,
  });

  return (
    <SpacingStack
      spacing={6}
      border="1px solid"
      borderColor="mono3"
      borderRadius={pxToRem(12)}
      padding={6}
    >
      <TextV2 typographyVariant="button2">{tq`Room Block Details`}</TextV2>

      <Flex
        marginTop={2}
        width="100%"
        flexDirection={'column'}
      >
        <FormControl
          width="100%"
          label={
            <ToolTipLabel
              label={tq`Booking Link`}
              tooltipTitle={tq`Booking Link`}
              tooltipContent={tq`Room block booking link where guests will go to reserve your room block. It usually includes your special discount code applied.`}
            />
          }
          isRequired={false}
          {...getFieldErrorProps('bookingUrl')}
        >
          <InputV2
            isDisabled={isDisabledForCouples || formik.values.isBookingLinkDisabled}
            placeholder={tq`www.examplehotel.com/myEvent`}
            {...formik.getFieldProps('bookingUrl')}
            {...getFieldErrorProps('bookingUrl')}
          />
        </FormControl>
        <CheckboxV2
          typographyVariant={'body2'}
          marginTop={5}
          isChecked={!!formik.values.isBookingLinkDisabled}
          {...formik.getFieldProps('isBookingLinkDisabled')}
          {...getFieldErrorProps('isBookingLinkDisabled')}
          onChange={e => {
            const isDisabled = e.target.checked;
            formik.setFieldValue('isBookingLinkDisabled', isDisabled);

            // Clear the booking URL when disabling the link
            if (isDisabled) {
              formik.setFieldValue('bookingUrl', '');
            }
          }}
        >
          {tq`We do not have a room block link`}
        </CheckboxV2>
      </Flex>
      {isInDialog ? (
        <Flex
          flexDirection={'row'}
          columnGap={5}
        >
          <FormControl
            width={'50%'}
            label={tq`Check in`}
            {...getFieldErrorProps('checkInDate')}
          >
            <DatePickerInput
              {...formik.getFieldProps('checkInDate')}
              minDate={new Date()}
              openToDate={new Date(formik.values.checkInDate ? formik.values.checkInDate.toString() : (formik.values.checkOutDate?.toString() ?? new Date().toString()))}
              onFocus={e => e.stopPropagation()}
              mask={null}
              endElement={null}
              isDisabled={isDisabledForCouples}
              startElement={<Calendar size={16} />}
              onChange={date => formik.setFieldValue('checkInDate', date)}
              overrides={{ Input: { Input: { props: { paddingLeft: '40px' } } } }}
              dateFormat="MMM dd, yyyy"
              placeholder={tq`Add a date`}
              isReadOnly={true}
              enableCloseOnSelect={true}
            />
          </FormControl>
          <FormControl
            width={'50%'}
            label={tq`Check out`}
            {...getFieldErrorProps('checkOutDate')}
          >
            <DatePickerInput
              {...formik.getFieldProps('checkOutDate')}
              minDate={new Date()}
              openToDate={new Date(formik.values.checkOutDate ? formik.values.checkOutDate.toString() : (formik.values.checkInDate?.toString() ?? new Date().toString()))}
              onFocus={e => e.stopPropagation()}
              mask={null}
              isDisabled={isDisabledForCouples}
              endElement={null}
              startElement={<Calendar size={16} />}
              onChange={date => formik.setFieldValue('checkOutDate', date)}
              overrides={{ Input: { Input: { props: { paddingLeft: '40px' } } } }}
              dateFormat="MMM dd, yyyy"
              placeholder={tq`Add a date`}
              isReadOnly={true}
              enableCloseOnSelect={true}
            />
          </FormControl>
        </Flex>
      ) : (
        <FormControl
          label={
            <ToolTipLabel
              label={tq`Dates`}
              tooltipTitle={tq`Dates`}
              tooltipContent={tq`Dates covered by your hotel room block and the nights you recommend guests book`}
            />
          }
          isRequired={false}
          isInvalid={!!formik.errors.checkInDate || !!formik.errors.checkOutDate}
        >
          <>
            <DateRangeFields
              label={{ startDate: tq`Check-In`, endDate: tq`Check-Out` }}
              value={{ startDate: formik.values.checkInDate, endDate: formik.values.checkOutDate }}
              onChange={onDateChange}
              appendPickerToBody
              isDisabled={isDisabledForCouples}
              enableCloseOnSelection={true}
            />
            {(!!formik.errors.checkInDate || !!formik.errors.checkOutDate) && (
              <Box
                marginTop={3}
                typographyVariant="label2"
                color="red6"
              >
                {formik.errors.checkInDate || formik.errors.checkOutDate}
              </Box>
            )}
          </>
        </FormControl>
      )}
      <FormControl
        {...getFieldErrorProps('cutoffDate')}
        label={
          <ToolTipLabel
            label={tq`Book By Date`}
            tooltipTitle={tq`Book By Date`}
            tooltipContent={tq`Enter the deadline for guests to book before your room block or special rate expires.`}
          />
        }
      >
        <DatePickerInput
          {...formik.getFieldProps('cutoffDate')}
          minDate={new Date()}
          onFocus={e => e.stopPropagation()}
          isDisabled={isDisabledForCouples}
          mask={null}
          endElement={null}
          startElement={<Calendar size={16} />}
          onChange={date => formik.setFieldValue('cutoffDate', date)}
          overrides={{ Input: { Input: { props: { paddingLeft: '40px', backgroundColor: isDisabledForCouples ? '#E0E0E0' : 'white' } } } }}
          dateFormat="E, MMM dd, yyyy"
          placeholder={tq`When does this deal expire?`}
          isReadOnly={true}
          enableCloseOnSelect={true}
        />
      </FormControl>
      <FormControl
        width="100%"
        label={
          <ToolTipLabel
            label={tq`Rooms Reserved`}
            tooltipTitle={tq`Rooms Reserved`}
            tooltipContent={tq`Number of rooms included in your room block at this hotel.`}
          />
        }
        isRequired={false}
        {...getFieldErrorProps('numberOfRoomsReserved')}
      >
        <InputV2
          disabled={isDisabledForCouples}
          style={{ backgroundColor: isDisabledForCouples ? '#E0E0E0' : 'white' }}
          type="number"
          {...formik.getFieldProps('numberOfRoomsReserved')}
          {...getFieldErrorProps('numberOfRoomsReserved')}
        />
      </FormControl>
      <FormControl
        width="100%"
        label={
          <ToolTipLabel
            label={tq`Group Price / Night`}
            tooltipTitle={tq`Group Price / Night`}
            tooltipContent={tq`The discounted nightly rate negotiated for your group so guests can see the special price.`}
          />
        }
        {...getFieldErrorProps('pricePerNight')}
      >
        <InputV2
          disabled={isDisabledForCouples}
          style={{ backgroundColor: isDisabledForCouples ? '#E0E0E0' : 'white', paddingLeft: pxToRem(32) }}
          startElement={
            <TextV2
              typographyVariant="body2"
              color="black"
            >
              $
            </TextV2>
          }
          type="number"
          {...formik.getFieldProps('pricePerNight')}
          {...getFieldErrorProps('pricePerNight')}
        />
      </FormControl>
      {isEditMode && !isSuperAdmin && Boolean(formik.values?.strikeoutPricePerNight) && (
        <Box width="100%">
          <FormControl
            {...getFieldErrorProps('strikeoutPricePerNight')}
            label={
              <ToolTipLabel
                label={tq`Standard Price / Night`}
                tooltipTitle={tq`Standard Price / Night`}
                tooltipContent={tq`The regular nightly rate before the group discount is applied.`}
              />
            }
          >
            <InputV2
              disabled={isDisabledForCouples}
              style={{ backgroundColor: isDisabledForCouples ? '#E0E0E0' : 'white', paddingLeft: pxToRem(32) }}
              startElement={
                <TextV2
                  typographyVariant="body2"
                  color="black"
                >
                  $
                </TextV2>
              }
              type="number"
              {...formik.getFieldProps('strikeoutPricePerNight')}
              {...getFieldErrorProps('strikeoutPricePerNight')}
            />
          </FormControl>
        </Box>
      )}
      {!isHotel && (
        <>
          <FormControl {...getFieldErrorProps('photoUrl')}>
            <Box
              padding={3}
              borderRadius={2}
              border="1px solid"
              borderColor="mono3"
              __css={{ boxShadow: !!formik.touched.photoUrl && !!formik.errors.photoUrl ? `0px 0 0px 2px ${theme.colors.inputBorderError}` : 'none' }}
            >
              <Box width="min-content">
                <PagePhoto
                  handleUpdatePhoto={onUpdateHotelPhoto}
                  handleDeletePhoto={onDeleteHotelPhoto}
                  handleResetPhoto={onResetHotelPhoto}
                  hasDefaultPhoto={hasDefaultPhoto}
                  loading={isUploadingPhoto}
                  photo={hotelPhoto}
                  label={hotelPhoto ? tq`Edit Photo` : tq`Add Photo`}
                />
              </Box>
            </Box>
          </FormControl>
          <StyledToggle
            label={tq`Spotlight`}
            name="isFavorite"
            overrides={{ Root: { props: { marginTop: 7 } } }}
            tooltip={{
              title: tq`Mark as Spotlight`,
              content: tq`Spotlight this hotel as your preferred choice so guests know where you'd like them to book.`,
            }}
            isChecked={!!formik.values.isFavorite}
            onChange={() => formik.setFieldValue('isFavorite', !formik.values.isFavorite)}
          />
        </>
      )}
      {isSuperAdmin && (
        <Box width="100%">
          <TextV2
            typographyVariant="label3"
            color="newUITest1"
            textTransform="uppercase"
          >
            {tq`Super Admins Only`}
          </TextV2>
          <FormControl
            marginTop={5}
            {...getFieldErrorProps('strikeoutPricePerNight')}
            label={
              <ToolTipLabel
                color="newUITest1"
                label={tq`Standard Price / Night`}
                tooltipTitle={tq`Standard Price / Night`}
                tooltipContent={tq`Standard price per night before group discount`}
              />
            }
          >
            <InputV2
              startElement={
                <TextV2
                  color="newUITest1"
                  typographyVariant="body2"
                >
                  $
                </TextV2>
              }
              style={{
                color: '#4951EF',
                paddingLeft: pxToRem(32),
              }}
              type="number"
              {...formik.getFieldProps('strikeoutPricePerNight')}
              {...getFieldErrorProps('strikeoutPricePerNight')}
            />
          </FormControl>
          <StyledToggle
            label={tq`Joy Hotel Room Block`}
            name="isJoyManagedRoomBlock"
            disabled={isEditMode}
            tooltip={{
              title: tq`Joy Hotel Room Block`,
              content: tq`Please check this only if this is a room block that was secured by the Joy room block service.`,
            }}
            overrides={{ Root: { props: { marginTop: 5, color: 'newUITest1' } }, Label: { props: { color: 'newUITest1' } } }}
            toggleBgColor="newUITest1"
            isChecked={!!formik.values.isJoyManagedRoomBlock}
            onChange={() => formik.setFieldValue('isJoyManagedRoomBlock', !formik.values.isJoyManagedRoomBlock)}
          />
        </Box>
      )}
    </SpacingStack>
  );
}

const ToolTipLabel = ({
  label,
  tooltipTitle,
  tooltipContent,
  color,
}: {
  label: string;
  tooltipTitle: string;
  tooltipContent: string;
  color?: ResponsiveValue<ThemeValue<'colors', Theme> | (string & {})>;
}) => {
  return (
    <Flex
      gap={1}
      alignItems="center"
      marginBottom={2}
    >
      <TextV2
        typographyVariant="label3"
        color={color}
      >
        {label}
      </TextV2>
      <TooltipV2
        overrides={{
          Content: { props: { paddingY: 4, paddingLeft: pxToRem(14), paddingRight: 4, borderRadius: pxToRem(12), color: 'white', backgroundColor: 'mono14' } },
        }}
        content={
          <Flex
            flexDirection="column"
            maxWidth={pxToRem(256)}
          >
            <Box typographyVariant="label3">{tooltipTitle}</Box>
            <Box
              typographyVariant="label2"
              color="mono4"
            >
              {tooltipContent}
            </Box>
          </Flex>
        }
        onMouseEnterDelay={500}
      >
        <Help
          size={16}
          color="mono10"
        />
      </TooltipV2>
    </Flex>
  );
};
